import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { selectUser, selectPartnersData } from "store/slices/app";
import * as api from "api/endpoints/batch";

import BatchSummaryMain from "../components/batch/BatchSummaryMain";

export default function BatchConfigurationPage() {
  const userData = useSelector(selectUser);
  const partnersData = useSelector(selectPartnersData);
  const loadData = (userData.isSuperUser && partnersData.isPartnerChanged) || !userData.isSuperUser;
  const [gridLoading, setGridLoading] = useState(false);

  const [reportsData, setReportsData] = useState([]);
  const [reportsDataResponse, setReportsDataResponse] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [epId, setEpId] = useState(userData.selectedPartner.id);

  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("date");

  const searchCallback = useCallback(handleSearch, [epId, fromDate, toDate, currentPage, order, orderBy]);

  useEffect(() => {
    loadData && searchCallback();
  }, [loadData, searchCallback]);

  useEffect(() => {
    setEpId(userData.selectedPartner.id);
    setCurrentPage(1);
    setFromDate(null);
    setToDate(null);
  }, [userData.selectedPartner.id]);

  async function handleSearch() {
    setGridLoading(true);

    try {
      const data = {
        pageNo: currentPage,
        pageSize: 10,
        epId: epId,
        fromDateTime:
          (fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : dayjs().subtract(1, "year").format("YYYY-MM-DD")) +
          "T00:00:00.000Z",
        toDateTime: (toDate ? dayjs(toDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD")) + "T23:59:59.000Z",
        sortBy: orderBy,
        sortOrder: order
      };

      const response = await api.getReports(data);
      const rows = response.details.map((f) => {
        return {
          ...f,
          items: []
        };
      });

      setReportsData(rows);
      setReportsDataResponse(response);
    } finally {
      setGridLoading(false);
    }
  }

  async function handleRowExpand(expandedRow) {
    const data = {
      epId: userData.selectedPartner.id,
      fileId: expandedRow.fileId
    };

    const response = await api.getReportDetails(data);

    const rows = reportsData.map((rw) =>
      rw.fileId === expandedRow.fileId ? { ...rw, items: response.details || [] } : rw
    );

    setReportsData(rows);
  }

  function handleFromDateSelect(value) {
    const date = value ? dayjs(value.toDate()).format() : null;
    setFromDate(date);
    setCurrentPage(1);
  }

  function handleToDateSelect(value) {
    const date = value ? dayjs(value.toDate()).format() : null;
    setToDate(date);
    setCurrentPage(1);
  }

  function handlePageChange(e, value) {
    setCurrentPage(value);
  }

  function handleClearFilter() {
    setFromDate(null);
    setToDate(null);
  }

  function handleRequestSort(event, property) {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  }

  const pagination = {
    page: currentPage,
    count: reportsDataResponse.page_count,
    onChange: handlePageChange
  };

  return (
    <BatchSummaryMain
      loading={gridLoading}
      reportsData={reportsData}
      pagination={pagination}
      loadData={loadData}
      fromDate={fromDate}
      toDate={toDate}
      order={order}
      orderBy={orderBy}
      onSearch={handleSearch}
      onExpand={handleRowExpand}
      onFromDateSelect={handleFromDateSelect}
      onToDateSelect={handleToDateSelect}
      onClearFilter={handleClearFilter}
      onRequestSort={handleRequestSort}
    />
  );
}
